// src/hooks/useServers.tsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Tables } from '@/integrations/supabase/types';
import { validateServerName, validateIPAddress, sanitizeInput } from '@/utils/securityValidation';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { useSocialMediaNotify } from '@/hooks/useSocialMediaNotify';
import { useLanguage } from '@/contexts/LanguageContext';

type Server = Tables<'servers'> & {
  games: Tables<'games'>;
  competitive_votes?: number;
  competitive_rank?: number;
  rank_change?: number;
};

type Lang = 'fr' | 'en' | 'es';

/** ---------------- i18n locale pour ce hook ---------------- */
const i18n = {
  fr: {
    add_success_title: 'Serveur ajouté !',
    add_success_visible_desc: 'Votre serveur est maintenant visible dans le classement.',
    add_success_pending_desc: "Votre serveur est en attente d'approbation.",
    add_error_title: 'Erreur',
    add_error_default: "Une erreur est survenue lors de l'ajout du serveur.",
    add_error_unique: 'Ce nom de serveur ou cette adresse IP existe déjà.',
    add_error_invalid: 'Les données fournies ne sont pas valides.',
    add_error_denied: "Vous n'avez pas les permissions nécessaires pour cette action.",
    add_error_required: 'Tous les champs requis doivent être remplis.',
    add_error_fk: "Le jeu sélectionné n'est pas valide.",
  },
  en: {
    add_success_title: 'Server added!',
    add_success_visible_desc: 'Your server is now visible in the ranking.',
    add_success_pending_desc: 'Your server is awaiting approval.',
    add_error_title: 'Error',
    add_error_default: 'An error occurred while adding the server.',
    add_error_unique: 'This server name or IP address already exists.',
    add_error_invalid: 'The data provided is not valid.',
    add_error_denied: 'You do not have the required permissions for this action.',
    add_error_required: 'All required fields must be filled in.',
    add_error_fk: 'The selected game is not valid.',
  },
  es: {
    add_success_title: '¡Servidor añadido!',
    add_success_visible_desc: 'Tu servidor ya es visible en el ranking.',
    add_success_pending_desc: 'Tu servidor está a la espera de aprobación.',
    add_error_title: 'Error',
    add_error_default: 'Se produjo un error al añadir el servidor.',
    add_error_unique: 'Este nombre de servidor o dirección IP ya existe.',
    add_error_invalid: 'Los datos proporcionados no son válidos.',
    add_error_denied: 'No tienes los permisos necesarios para esta acción.',
    add_error_required: 'Todos los campos obligatorios deben completarse.',
    add_error_fk: 'El juego seleccionado no es válido.',
  },
} as const;

/** Normalise une langue potentielle (fr, fr-FR, FR, etc.) vers 'fr' | 'en' | 'es' */
function normalizeLang(input?: string | null): Lang {
  const v = String(input ?? '').toLowerCase().trim();
  if (v.startsWith('en')) return 'en';
  if (v.startsWith('es')) return 'es';
  return 'fr';
}

/** Essaie de déduire la langue de l'URL (/en/..., /es/..., /fr/...) */
function getLangFromPath(pathname?: string | null): Lang | undefined {
  if (!pathname) return undefined;
  const seg = pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  if (seg === 'en' || seg === 'es' || seg === 'fr') return seg as Lang;
  return undefined;
}

/** Résolution robuste de la langue côté client */
function resolveClientLanguage(ctxLanguage?: string | null): Lang {
  const pathLang = typeof window !== 'undefined' ? getLangFromPath(window.location.pathname) : undefined;
  if (pathLang) return pathLang;

  const htmlLang = typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null;
  if (htmlLang) return normalizeLang(htmlLang);

  if (ctxLanguage) return normalizeLang(ctxLanguage);

  const navLang = typeof navigator !== 'undefined' ? navigator.language : 'fr';
  return normalizeLang(navLang);
}

/** Helper pour récupérer les libellés traduits */
function t(lang: Lang): any {
  return i18n[lang];
}
/** ---------------------------------------------------------- */

export const useServers = (
  gameId?: string,
  page: number = 1,
  limit: number = 25,
  sortBy: 'default' | 'votes' | 'popular' | 'recent' | 'clicks' | 'competitive' = 'default',
  tags?: string[],
  prestigeFilter: 'all' | 'prestige-only' | 'regular-only' = 'all',
  isCompetitiveMode: boolean = false
) => {
  return useQuery({
    queryKey: ['servers', gameId, page, limit, sortBy, tags, prestigeFilter, isCompetitiveMode],
    queryFn: async () => {
      // console.log('Fetching servers with params:', { gameId, page, limit, sortBy, tags, prestigeFilter, isCompetitiveMode });

      const offset = (page - 1) * limit;

      // --------------------------
      // Mode COMPÉTITIF (inchangé)
      // --------------------------
      if (isCompetitiveMode) {
        const { data: activeEvent } = await supabase
          .from('competitive_events')
          .select('*')
          .eq('is_active', true)
          .eq('is_finished', false)
          .lte('start_date', new Date().toISOString())
          .gte('end_date', new Date().toISOString())
          .single();

        if (!activeEvent) {
          return {
            servers: [] as Server[],
            totalCount: 0,
            totalPages: 0,
            currentPage: page,
            hasNextPage: false,
            hasPreviousPage: page > 1,
          };
        }

        let participationQuery = supabase
          .from('competitive_event_participations')
          .select(
            `
            *,
            servers:server_id!inner (
              *,
              games (*)
            )
          `,
            { count: 'exact' }
          )
          .eq('event_id', activeEvent.id)
          .order('votes_count', { ascending: false });

        if (gameId) {
          participationQuery = participationQuery.eq('servers.game_id', gameId);
        }

        participationQuery = participationQuery.range(offset, offset + limit - 1);

        const { data: participations, error, count } = await participationQuery;

        if (error) {
          // console.error('Error fetching competitive participations:', error);
          throw error;
        }

        const transformedData: Server[] =
          participations?.map((p, idx) => {
            // Defensive: never leak api_token even if the join returns it
            const { api_token: _ignored, ...serverSafe } = (p.servers || {}) as any;
            return {
              ...serverSafe,
              competitive_votes: p.votes_count,
              competitive_rank: offset + idx + 1,
            };
          }) || [];


        return {
          servers: transformedData,
          totalCount: count || 0,
          totalPages: Math.ceil((count || 0) / limit),
          currentPage: page,
          hasNextPage: page < Math.ceil((count || 0) / limit),
          hasPreviousPage: page > 1,
        };
      }

      // --------------------------
      // Mode NORMAL — 2 requêtes
      // --------------------------
      const nowIso = new Date().toISOString();

      // 1) Tous les PRESTIGE qui matchent les filtres (sans pagination)
      let prestigeQuery = supabase
        .from('servers_public')
        .select(
          `
          *,
          games (*)
        `,
          { count: 'exact' }
        )
        .eq('is_sponsored', true)
        .gt('prestige_expires_at', nowIso);

      if (gameId) prestigeQuery = prestigeQuery.eq('game_id', gameId);
      if (tags && tags.length > 0) prestigeQuery = prestigeQuery.overlaps('tags', tags);

      // Tri initial (indolore, ordre final géré plus bas)
      switch (sortBy) {
        case 'votes':
        case 'default':
        case 'competitive':
          prestigeQuery = prestigeQuery.order('monthly_votes', { ascending: false });
          break;
        case 'popular':
          prestigeQuery = prestigeQuery.order('online_players', { ascending: false });
          break;
        case 'recent':
          prestigeQuery = prestigeQuery.order('created_at', { ascending: false });
          break;
        case 'clicks':
          prestigeQuery = prestigeQuery.order('id', { ascending: true }); // neutre
          break;
        default:
          prestigeQuery = prestigeQuery.order('monthly_votes', { ascending: false });
      }

      const { data: prestigeData, error: prestigeError, count: prestigeCountRaw } = await prestigeQuery;
      if (prestigeError) {
        // console.error('Error fetching prestige servers:', prestigeError);
        throw prestigeError;
      }
      const prestigeServersAll = (prestigeData || []) as Server[];
      const prestigeCount = prestigeCountRaw || prestigeServersAll.length;
      const prestigeIds = prestigeServersAll.map((s) => s.id);

      // 2) RÉGULIERS : exclure IDs prestige, fenêtre élargie
      const regularWindow = offset + limit + prestigeCount;

      let regularQuery = supabase
        .from('servers_public')
        .select(
          `
          *,
          games (*)
        `,
          { count: 'exact' }
        );

      if (gameId) regularQuery = regularQuery.eq('game_id', gameId);
      if (tags && tags.length > 0) regularQuery = regularQuery.overlaps('tags', tags);

      if (prestigeIds.length > 0) {
        const idList = `(${prestigeIds.map((id) => `"${id}"`).join(',')})`;
        regularQuery = regularQuery.not('id', 'in', idList);
      }

      switch (sortBy) {
        case 'votes':
        case 'default':
        case 'competitive':
          regularQuery = regularQuery.order('monthly_votes', { ascending: false });
          break;
        case 'popular':
          regularQuery = regularQuery.order('online_players', { ascending: false });
          break;
        case 'recent':
          regularQuery = regularQuery.order('created_at', { ascending: false });
          break;
        case 'clicks':
          regularQuery = regularQuery.order('id', { ascending: true }); // neutre, tri après
          break;
        default:
          regularQuery = regularQuery.order('monthly_votes', { ascending: false });
      }

      regularQuery = regularQuery.range(0, Math.max(0, regularWindow - 1));

      const { data: regularData, error: regularError, count: regularCount } = await regularQuery;
      if (regularError) {
        // console.error('Error fetching regular servers:', regularError);
        throw regularError;
      }

      // Fusion initiale (ordre final ajusté ensuite)
      let servers: Server[] = [...prestigeServersAll, ...(regularData as Server[])];

      // 3) Tri "clicks" global si demandé
      if (sortBy === 'clicks') {
        // console.log('Fetching click counts for servers (prestige + regular)...');
        const serverIdsForClicks = servers.map((s) => s.id);
        const { data: clicksData, error: clicksError } = await supabase
          .from('server_clicks')
          .select('server_id')
          .in('server_id', serverIdsForClicks);

        if (clicksError) {
          // console.error('Error fetching clicks:', clicksError);
        } else {
          const clickCounts = clicksData.reduce((acc, click) => {
            acc[click.server_id] = (acc[click.server_id] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          servers = servers.sort((a, b) => (clickCounts[b.id] || 0) - (clickCounts[a.id] || 0));
          // console.log('Servers sorted by clicks:', clickCounts);
        }
      }

      // 4) Re-séparation prestige / réguliers (au cas où le tri clicks a changé l'ordre)
      const prestigeServers = servers.filter(
        (s) => s.is_sponsored && s.prestige_expires_at && new Date(s.prestige_expires_at) > new Date()
      );
      const regularServers = servers.filter(
        (s) => !s.is_sponsored || !s.prestige_expires_at || new Date(s.prestige_expires_at) <= new Date()
      );

      // 4.5) Récupérer les VRAIS monthly_votes dynamiques (même calcul que ServerCardStats)
      let realMonthlyVotes: Record<string, number> = {};
      if (sortBy === 'votes' || sortBy === 'default') {
        try {
          const { data: realVotesData, error: realVotesError } = await supabase
            .rpc('get_servers_with_real_monthly_votes', {
              p_game_id: gameId || null,
              p_limit: 500,
              p_offset: 0,
              p_tags: tags && tags.length > 0 ? tags : null
            });
          
          if (!realVotesError && realVotesData) {
            realMonthlyVotes = (realVotesData as { server_id: string; real_monthly_votes: number }[]).reduce((acc, item) => {
              acc[item.server_id] = Number(item.real_monthly_votes) || 0;
              return acc;
            }, {} as Record<string, number>);
          }
        } catch (error) {
          console.warn('Could not fetch real monthly votes, using cached values:', error);
        }
      }

      // 5) Logique d'affichage finale
      let filteredServers: Server[];

      switch (prestigeFilter) {
        case 'prestige-only': {
          filteredServers = [...prestigeServers];
          if (sortBy === 'votes') {
            filteredServers = filteredServers.sort((a, b) => (b.monthly_votes || 0) - (a.monthly_votes || 0));
          } else if (sortBy !== 'clicks') {
            filteredServers = [...filteredServers].sort(() => Math.random() - 0.5);
          }
          break;
        }

        case 'regular-only': {
          filteredServers = [...regularServers];
          // Trier par les VRAIS monthly_votes si disponibles
          if (sortBy === 'votes' || sortBy === 'default') {
            filteredServers = filteredServers.sort((a, b) => {
              const aVotes = realMonthlyVotes[a.id] ?? (a.monthly_votes || 0);
              const bVotes = realMonthlyVotes[b.id] ?? (b.monthly_votes || 0);
              return bVotes - aVotes;
            });
          }
          break;
        }

        case 'all':
        default: {
          // Prestige : TOUJOURS en ordre aléatoire (peu importe le tri sélectionné)
          const shuffledPrestige = [...prestigeServers].sort(() => Math.random() - 0.5);
          
          if (sortBy === 'votes' || sortBy === 'default') {
            // Réguliers : triés par les VRAIS monthly_votes dynamiques (même calcul que l'affichage)
            const sortedRegulars = [...regularServers].sort((a, b) => {
              const aVotes = realMonthlyVotes[a.id] ?? (a.monthly_votes || 0);
              const bVotes = realMonthlyVotes[b.id] ?? (b.monthly_votes || 0);
              return bVotes - aVotes;
            });
            filteredServers = [...shuffledPrestige, ...sortedRegulars];
          } else if (sortBy === 'clicks') {
            // Respecter l'ordre clicks existant pour les réguliers
            const regularOrdered = servers.filter((s) => regularServers.some((r) => r.id === s.id));
            filteredServers = [...shuffledPrestige, ...regularOrdered];
          } else {
            // Autres tris (popular, recent) : garder l'ordre de la DB pour les réguliers
            filteredServers = [...shuffledPrestige, ...regularServers];
          }
          break;
        }
      }

      // 6) Récupération des changements de rang
      let rankChanges: Record<string, number> = {};
      if (!isCompetitiveMode) {
        try {
          if (gameId) {
            // Classement PAR JEU
            const { data: rankData, error: rankError } = await supabase
              .rpc('get_servers_rank_changes', { p_game_id: gameId });
            
            if (!rankError && rankData) {
              rankChanges = rankData.reduce((acc, item) => {
                acc[item.server_id] = item.rank_change;
                return acc;
              }, {} as Record<string, number>);
            }
          } else {
            // Classement GLOBAL (tous les jeux)
            const { data: rankData, error: rankError } = await supabase
              .rpc('get_servers_rank_changes_global');
            
            if (!rankError && rankData) {
              rankChanges = rankData.reduce((acc, item) => {
                acc[item.server_id] = item.rank_change;
                return acc;
              }, {} as Record<string, number>);
            }
          }
        } catch (error) {
          console.warn('Could not fetch rank changes:', error);
        }
      }

      // 7) Pagination après tri global et ajout des rank_change
      const start = offset;
      const end = offset + limit;
      const finalServers = filteredServers.slice(start, end).map(server => ({
        ...server,
        rank_change: rankChanges[server.id] ?? undefined
      }));

      // 8) totalCount exact (prestige + réguliers) pour une pagination correcte
      let totalCount = 0;
      {
        let prestigeCountQuery = supabase
          .from('servers_public')
          .select('id', { count: 'exact', head: true })
          .eq('is_sponsored', true)
          .gt('prestige_expires_at', nowIso);
        if (gameId) prestigeCountQuery = prestigeCountQuery.eq('game_id', gameId);
        if (tags && tags.length > 0) prestigeCountQuery = prestigeCountQuery.overlaps('tags', tags);
        const { count: prestigeExactCount, error: prestigeCountError } = await prestigeCountQuery;
        // if (prestigeCountError) console.warn('Count prestige error (fallback to fetched length)', prestigeCountError);

        let totalCountQuery = supabase.from('servers_public').select('id', { count: 'exact', head: true });
        if (gameId) totalCountQuery = totalCountQuery.eq('game_id', gameId);
        if (tags && tags.length > 0) totalCountQuery = totalCountQuery.overlaps('tags', tags);
        const { count: totalExactCount, error: totalCountError2 } = await totalCountQuery;
        // if (totalCountError2) console.warn('Total count error', totalCountError2);

        const prestigeC = prestigeExactCount ?? prestigeServersAll.length;
        const totalC = totalExactCount ?? prestigeServersAll.length + (regularCount || 0);
        totalCount = (prestigeC || 0) + Math.max(0, (totalC || 0) - (prestigeC || 0));
      }

      return {
        servers: finalServers,
        totalCount,
        totalPages: Math.ceil(totalCount / limit),
        currentPage: page,
        hasNextPage: page < Math.ceil(totalCount / limit),
        hasPreviousPage: page > 1,
      };
    },
    // Réduit pour refléter vite les expirations Prestige
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
};

export const useVoteServer = () => {
  const queryClient = useQueryClient();
  const { logSecurityEvent } = useSecurityAudit();

  return useMutation({
    mutationFn: async ({
      serverId,
      ipHash,
      playerName,
    }: {
      serverId: string;
      ipHash: string;
      playerName: string;
    }) => {
      // console.log('🔄 Starting vote mutation with params:', {
      //   serverId,
      //   ipHash: ipHash.substring(0, 10) + '...',
      //   playerName,
      // });

      if (!serverId || !ipHash || !playerName) {
        // console.error('❌ Missing required parameters:', { serverId: !!serverId, ipHash: !!ipHash, playerName: !!playerName });
        throw new Error('Missing required parameters');
      }

      const sanitizedPlayerName = sanitizeInput(playerName.trim());
      if (sanitizedPlayerName.length < 2 || sanitizedPlayerName.length > 50) {
        // console.error('❌ Invalid player name length:', sanitizedPlayerName.length);
        throw new Error('Invalid player name length');
      }

      // console.log('🔍 Checking existing votes...');
      const { data: existingVote, error: voteCheckError } = await supabase
        .from('votes')
        .select('id, voted_at')
        .eq('server_id', serverId)
        .eq('ip_hash', ipHash)
        .eq('vote_date', new Date().toISOString().split('T')[0])
        .single();

      if (voteCheckError && voteCheckError.code !== 'PGRST116') {
        // console.error('❌ Error checking existing votes:', voteCheckError);
        throw new Error(`Database error: ${voteCheckError.message}`);
      }

      if (existingVote) {
        // console.log('⚠️ User already voted today:', existingVote);
        const lastVoteTime = new Date(existingVote.voted_at);
        const nextVoteTime = new Date(lastVoteTime.getTime() + 2 * 60 * 60 * 1000);
        const now = new Date();
        const hoursLeft = Math.ceil((nextVoteTime.getTime() - now.getTime()) / (1000 * 60 * 60));

        logSecurityEvent({
          eventType: 'vote_attempt_blocked',
          eventData: { serverId, reason: 'already_voted_today', hoursLeft },
        });

        throw new Error(`WAIT_TIME:${hoursLeft}`);
      }

      console.log('🔍 Checking server exists and is approved...');
      const { data: server, error: serverError } = await supabase
        .from('servers')
        .select('id, is_approved')
        .eq('id', serverId)
        .eq('is_approved', true)
        .single();

      if (serverError) {
        console.error('❌ Error checking server:', serverError);
        throw new Error(`Server check error: ${serverError.message}`);
      }

      if (!server) {
        console.error('❌ Server not found or not approved');
        logSecurityEvent({
          eventType: 'vote_attempt_blocked',
          eventData: { serverId, reason: 'server_not_found_or_not_approved' },
        });
        throw new Error('Server not found or not approved');
      }

      console.log('✅ Server found and approved:', server);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('❌ Error getting user:', userError);
      }

      console.log('📝 Inserting vote...');
      const { data, error } = await supabase
        .from('votes')
        .insert({
          server_id: serverId,
          ip_hash: ipHash,
          user_agent_hash: navigator.userAgent.substring(0, 500),
          player_name: sanitizedPlayerName,
          user_id: user?.id || null,
        })
        .select();

      if (error) {
        console.error('❌ Error inserting vote:', error);
        logSecurityEvent({
          eventType: 'vote_failed',
          eventData: { serverId, error: error.message },
        });
        throw new Error(`Vote insertion failed: ${error.message}`);
      }

      console.log('✅ Vote inserted successfully:', data);
      logSecurityEvent({
        eventType: 'vote_successful',
        eventData: { serverId, playerName: sanitizedPlayerName },
      });

      return data;
    },
    onSuccess: (data) => {
      console.log('🎉 Vote mutation successful:', data);
      // Invalider toutes les variantes de requêtes servers/stats
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (error: any) => {
      console.error('❌ Vote mutation error:', error);

      if (typeof error?.message === 'string' && error.message.startsWith('WAIT_TIME:')) {
        const hoursLeft = Number(error.message.split(':')[1] || '2');
        toast({
          title: '⏰ Vous avez déjà voté !',
          description: `Vous pourrez voter à nouveau dans ${hoursLeft} heure${hoursLeft > 1 ? 's' : ''}.`,
          variant: 'destructive',
        });
      } else if (error?.code === '23505' || /already voted/i.test(error?.message || '')) {
        toast({
          title: "⏰ Déjà voté aujourd'hui",
          description: "Vous avez déjà voté pour ce serveur aujourd'hui. Revenez plus tard !",
          variant: 'destructive',
        });
      } else if (
        /validation failed|not found|not approved/i.test(error?.message || '') ||
        error?.code === '42501'
      ) {
        toast({
          title: '❌ Vote non autorisé',
          description: "Ce vote n'est pas autorisé pour le moment.",
          variant: 'destructive',
        });
      } else {
        toast({
          title: '❌ Erreur de vote',
          description: `Erreur détaillée: ${error?.message || 'Inconnue'}`,
          variant: 'destructive',
        });
      }
    },
  });
};

export const useAddServer = () => {
  const queryClient = useQueryClient();
  const { logSecurityEvent } = useSecurityAudit();
  const { triggerSocialNotification } = useSocialMediaNotify();
  const { language } = useLanguage();
  const lang: Lang = resolveClientLanguage(language);
  const L = t(lang);

  return useMutation({
    mutationFn: async (serverData: {
      name: string;
      game_id: string;
      description: string;
      ip_address: string;
      version?: string;
      discord_url?: string;
      website_url?: string;
      tags?: string[];
      is_sponsored?: boolean;
      sponsor_tier?: string | null;
      custom_color?: string | null;
      logo_image_url?: string | null;
      banner_image_url?: string | null;
      description_image_url?: string | null;
      is_prestige_bonus?: boolean;
    }) => {
      console.log('🚀 Starting server creation with data:', serverData);

      if (!validateServerName(serverData.name)) {
        console.log('❌ Server name validation failed:', serverData.name);
        throw new Error('Invalid server name - must be 3-100 characters');
      }

      if (serverData.ip_address && !validateIPAddress(serverData.ip_address)) {
        console.log('❌ IP address validation failed:', serverData.ip_address);
        throw new Error('Invalid IP address format');
      }

      const sanitizedData = {
        ...serverData,
        name: sanitizeInput(serverData.name.trim()),
        description: sanitizeInput(serverData.description || ''),
        version: serverData.version ? sanitizeInput(serverData.version.trim()) : null,
      };

      console.log('🧹 Sanitized data:', sanitizedData);

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.log('❌ Authentication failed - no user');
        throw new Error('Authentication required');
      }

      console.log('✅ User authenticated:', user.user.id);

      const { data: autoApproveData, error: settingsError } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'auto_approve_servers')
        .single();

      if (settingsError) {
        console.log('⚠️ Settings error (using default):', settingsError);
      }

      const autoApprove = settingsError || !autoApproveData ? true : autoApproveData?.setting_value === true;
      console.log('⚙️ Auto approve setting:', autoApprove);

      const finalData = {
        name: sanitizedData.name,
        game_id: sanitizedData.game_id,
        description: sanitizedData.description,
        ip_address: sanitizedData.ip_address || 'N/A',
        version: sanitizedData.version || undefined,
        discord_url: sanitizedData.discord_url || undefined,
        website_url: sanitizedData.website_url || undefined,
        tags: sanitizedData.tags || [],
        logo_image_url: sanitizedData.logo_image_url || undefined,
        banner_image_url: sanitizedData.banner_image_url || undefined,
        description_image_url: sanitizedData.description_image_url || undefined,
        is_prestige_bonus: sanitizedData.is_prestige_bonus || false,
        is_sponsored: false,
        sponsor_tier: null,
        custom_color: null,
        owner_id: user.user.id,
        is_approved: autoApprove,
        is_hidden: false,
      };

      console.log('💾 Final data for insertion:', finalData);
      console.log('✅ Setting is_approved to:', autoApprove);
      console.log('🔍 Site setting auto_approve_servers:', autoApproveData);
      console.log('🔍 Settings error (if any):', settingsError);

      const { data, error } = await supabase.from('servers').insert(finalData).select();

      if (error) {
        console.error('❌ Database error in useAddServer:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });

        logSecurityEvent({
          eventType: 'server_creation_failed',
          eventData: {
            error: error.message,
            code: error.code,
            details: error.details,
          },
        });
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('No server data returned after insertion');
      }

      console.log('✅ Server created successfully:', data[0]);
      console.log('📊 Created server details:', {
        id: data[0]?.id,
        name: data[0]?.name,
        is_approved: data[0]?.is_approved,
        is_hidden: data[0]?.is_hidden,
        owner_id: data[0]?.owner_id,
      });

      const { data: serverWithGame, error: fetchError } = await supabase
        .from('servers')
        .select(
          `
          *,
          games (*)
        `
        )
        .eq('id', data[0].id)
        .single();

      if (fetchError) {
        console.error('⚠️ Could not fetch server with game relation:', fetchError);
        logSecurityEvent({
          eventType: 'server_created',
          eventData: { serverId: data[0].id },
        });
        return data;
      }

      console.log('✅ Server with game relation fetched:', serverWithGame);

      logSecurityEvent({
        eventType: 'server_created',
        eventData: { serverId: data[0]?.id },
      });

      return [serverWithGame];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });

      const isAutoApproved = data[0]?.is_approved;
      const serverId = data[0]?.id;

      // Déclencher les notifications sur les réseaux sociaux si le serveur est approuvé
      if (isAutoApproved && serverId) {
        try {
          console.log('📢 Triggering social media notifications for server:', serverId);
          console.log('🏁 Server auto-approved, starting social media notifications...');
          
          // Appel asynchrone à la fonction de notification (ne pas bloquer l'UI)
          triggerSocialNotification({ serverId }).catch(error => {
            console.error('⚠️ Social media notification failed:', error);
            if (error.message) console.error('Error message:', error.message);
            if (error.details) console.error('Error details:', error.details);
          });
        } catch (error) {
          console.error('⚠️ Failed to trigger social media notifications:', error);
        }
      } else {
        console.log('📵 Server not auto-approved or no serverId, skipping social media notifications', { isAutoApproved, serverId });
      }

      // --- TOAST MULTILINGUE ---
      toast({
        title: L.add_success_title,
        description: isAutoApproved ? L.add_success_visible_desc : L.add_success_pending_desc,
      });
    },
    onError: (error: any) => {
      console.error('Error in useAddServer:', error);
      console.error('Full error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });

      let errorMessage: string = t(lang).add_error_default;

      if (error.message?.includes('unique constraint') || error.code === '23505') {
        errorMessage = t(lang).add_error_unique;
      } else if (error.message?.includes('invalid input') || error.message?.includes('check constraint')) {
        errorMessage = t(lang).add_error_invalid;
      } else if (error.message?.includes('permission denied') || error.code === '42501') {
        errorMessage = t(lang).add_error_denied;
      } else if (error.message?.includes('null value in column') || error.code === '23502') {
        errorMessage = t(lang).add_error_required;
      } else if (error.message?.includes('foreign key constraint') || error.code === '23503') {
        errorMessage = t(lang).add_error_fk;
      }

      console.log('Translated error message:', errorMessage);

      toast({
        title: t(lang).add_error_title,
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};
